import { getWorkMode, homeForMode, onlyWorkMode, pathAllowedByModes, type WorkMode } from './workMode'
import { unitSpecAllows, type UnitAccessSpec } from '@shoptrack/common/unit-permissions'
import type { StaffInfo } from './auth'
// Vai trò (GĐ4): Đại lý, CTV, Shop — đều bán hàng (§2.1).
// Roles: 'owner' (chủ đơn vị), 'agency', 'ctv'
export type Role = 'owner' | 'agency' | 'ctv'
export type TenantType = 'super_admin' | 'admin' | 'agency' | 'ctv' | 'shop'

// Định nghĩa quyền cho từng chức năng theo bảng thiết kế (SRS v2)
// Hệ thống phân quyền hiện tại neo ở cấp Shop là thấp nhất
const ROLE_FEATURES: Record<Role, string[]> = {
  owner: ['*'],  // Toàn quyền trong tenant Shop hiện tại
  agency: ['*'], // Toàn quyền trong tenant Đại lý
  ctv: ['*'],    // Toàn quyền trong tenant CTV
}

// ─── Nhân viên của đơn vị (docs/KE_HOACH_THANH_VIEN_DON_VI.md) ────────────────────────────────
// Trang nhân viên mở được = trang chủ đơn vị mở được (theo loại đơn vị) VÀ đủ quyền dưới đây. Trang không có trong
// bảng = không mở (chặn, không lộ). Máy chủ vẫn kiểm từng request — bảng này chỉ để ẩn menu / chuyển trang.
const STAFF_PAGES: { path: string; spec: UnitAccessSpec | 'owner' }[] = [
  { path: '/app', spec: 'money.view' },
  { path: '/orders/new', spec: 'orders.create' },
  { path: '/orders', spec: 'orders.view' },
  { path: '/ship-payments', spec: 'orders.ship_prepay' },
  { path: '/products/new', spec: 'products.manage' },
  { path: '/products/edit', spec: 'products.manage' },
  { path: '/products', spec: 'products.view' },
  { path: '/customers', spec: 'customers.view' },
  { path: '/reconcile', spec: 'money.view' },
  { path: '/reports', spec: 'money.view' },
  { path: '/rates', spec: { any: ['rates.view', 'network.pricing'] } },
  { path: '/commissions/me', spec: 'money.view' },
  { path: '/commissions', spec: { all: ['network.view', 'money.view'] } },
  { path: '/settings', spec: 'member' },
  { path: '/shipping', spec: 'owner' },
  { path: '/agency/orders', spec: { all: ['orders.view', 'network.view'] } },
  { path: '/agency', spec: { all: ['network.view', 'money.view'] } },
  { path: '/ctv/orders', spec: { all: ['orders.view', 'network.view'] } },
  { path: '/ctv', spec: { all: ['network.view', 'money.view'] } },
  { path: '/network/upgrade-requests', spec: 'owner' },
  { path: '/network/announcements', spec: 'network.announce' },
  { path: '/network', spec: 'network.view' },
  { path: '/upgrade', spec: 'owner' },
  { path: '/feedback', spec: 'member' },
  { path: '/notifications', spec: 'member' },
  // Gom Đơn (features/gomdon): nhân viên phải có quyền lên đơn — engine cũng chặn (403 no_access)
  { path: '/inbox', spec: 'orders.create' },
  { path: '/comments', spec: 'orders.create' },
  { path: '/chat-channels', spec: 'orders.create' },
]

/** Nhân viên mở được trang `path` với các quyền `permissions` không (chưa xét loại đơn vị). */
export function staffCanOpen(path: string, permissions: readonly string[]): boolean {
  const p = path === '/' ? '/app' : /^\/products\/[^/]+\/edit$/.test(path) ? '/products/edit' : path
  let hit: (typeof STAFF_PAGES)[number] | null = null
  for (const x of STAFF_PAGES) {
    if ((p === x.path || p.startsWith(x.path + '/')) && (!hit || x.path.length > hit.path.length)) hit = x
  }
  if (!hit || hit.spec === 'owner') return false
  return unitSpecAllows(hit.spec, new Set(permissions))
}

// Menu của từng chế độ (chỉ đường dẫn) — để tìm trang đầu tiên nhân viên được vào
const STAFF_SELL_HOME = ['/app', '/orders', '/products', '/customers', '/rates', '/reconcile', '/reports']
const STAFF_MANAGE_HOME: Record<string, string[]> = {
  agency: ['/agency/overview', '/network', '/rates', '/commissions', '/reconcile', '/reports'],
  ctv: ['/ctv/dashboard', '/network', '/rates', '/commissions/me', '/reconcile', '/reports'],
}

/** Trang đầu tiên nhân viên được vào: theo chế độ đang dùng, không có thì chế độ kia, cuối cùng là Cài đặt. */
export function staffHomeRoute(tenantType: string | null | undefined, staff: StaffInfo | null | undefined, workModes?: readonly WorkMode[] | null): string {
  const manage = (tenantType && STAFF_MANAGE_HOME[tenantType]) || []
  const order = manage.length && getWorkMode() === 'manage' ? [manage, STAFF_SELL_HOME] : [STAFF_SELL_HOME, manage]
  for (const list of order) {
    const hit = list.find((path) => hasAccess('staff', path, tenantType, staff, workModes))
    if (hit) return hit
  }
  return '/settings'
}

/**
 * Kiểm tra xem role và tenantType hiện tại có quyền truy cập vào path không.
 * `staff` — vai trò + quyền khi tài khoản là nhân viên của đơn vị (role 'staff').
 * `workModes` — giao diện cấp trên bật cho Đại lý / CTV (docs/KE_HOACH_CHON_GIAO_DIEN_CAP_DUOI.md).
 */
export function hasAccess(role: string | undefined | null, path: string, tenantType?: string | null, staff?: StaffInfo | null, workModes?: readonly WorkMode[] | null): boolean {
  // Nhân viên: trang phải hợp loại đơn vị (và giao diện) như của chủ, và đủ quyền chủ cấp
  if (role === 'staff') {
    return hasAccess('owner', path, tenantType, null, workModes) && staffCanOpen(path, staff?.permissions ?? [])
  }

  // Trang Góp ý mở cho mọi tài khoản
  if (path.startsWith('/feedback')) return true

  // Chuẩn hóa /app thành / để kiểm tra đồng nhất
  const normalizedPath = path === '/app' ? '/' : path

  // Super Admin & Admin toàn quyền
  if (tenantType === 'super_admin' || tenantType === 'admin') return true

  // Đại lý / CTV: trang chỉ thuộc giao diện cấp trên đã tắt
  if ((tenantType === 'agency' || tenantType === 'ctv') && !pathAllowedByModes(path, workModes)) return false

  // CTV: module CTV + mạng lưới (Shop của mình) + bán hàng (CTV cũng bán — §2.1), gồm
  // Tổng quan bán hàng `/app` ở chế độ Bán hàng (docs/KE_HOACH_CHIA_GIAO_DIEN_QUAN_LY_BAN_HANG.md)
  if (tenantType === 'ctv' || role === 'ctv') {
    if (normalizedPath.startsWith('/agency')) return false
    return true
  }

  // Đại lý: module Đại lý + mạng lưới + bán hàng (không vào module CTV)
  if (tenantType === 'agency') {
    if (normalizedPath.startsWith('/ctv')) return false
    return true
  }

  // Shop: chỉ module bán hàng (không có mạng lưới, không có hoa hồng CTV)
  if (tenantType === 'shop') {
    if (
      normalizedPath.startsWith('/agency') ||
      normalizedPath.startsWith('/ctv') ||
      normalizedPath.startsWith('/network') ||
      normalizedPath.startsWith('/commissions')
    ) return false
    const allowed = ROLE_FEATURES[(role as Role) || 'owner'] || []
    if (allowed.includes('*')) return true
    const base = normalizedPath === '/' ? '/' : `/${normalizedPath.split('/')[1]}`
    return allowed.includes(base) || allowed.includes(normalizedPath)
  }

  if (!role) return false
  const allowed = ROLE_FEATURES[role as Role] || []
  if (allowed.includes('*')) return true
  
  const base = normalizedPath === '/' ? '/' : `/${normalizedPath.split('/')[1]}`
  return allowed.includes(base) || allowed.includes(normalizedPath)
}

/**
 * Xác định trang đích mặc định (Home route) tương ứng với từng cấp bậc tài khoản
 */
export function getHomeRouteForTenant(tenantType?: string | null, role?: string | null, staff?: StaffInfo | null, workModes?: readonly WorkMode[] | null): string {
  if (role === 'staff') return staffHomeRoute(tenantType, staff, workModes)
  if (tenantType === 'super_admin' || tenantType === 'admin') return '/agency/overview'
  // Đại lý / CTV: giao diện duy nhất cấp trên bật, không thì chế độ dùng lần trước (Quản lý / Bán hàng)
  const unit = tenantType === 'agency' ? 'agency' : tenantType === 'ctv' || role === 'ctv' ? 'ctv' : null
  if (unit) return homeForMode(unit, onlyWorkMode(workModes) ?? getWorkMode())
  return '/app'
}

/**
 * Lấy tên danh mục cấp bậc hiển thị cho UI (ví dụ sidebar header)
 */
export function getAccountCategoryName(tenantType?: string | null): string {
  switch (tenantType) {
    case 'super_admin': return 'Super Admin'
    case 'admin': return 'Quản Trị Viên'
    case 'agency': return 'Đại Lý Phân Phối'
    case 'ctv': return 'Cộng Tác Viên (CTV)'
    case 'shop': return 'Shop / Điểm Bán'
    default: return 'Shop / Điểm Bán'
  }
}

/**
 * Lấy danh sách tên role hiển thị cho UI theo ngữ cảnh tenantType
 */
export function getRoleDisplayName(role: string, tenantType?: string | null, staffRoleName?: string | null): string {
  if (role === 'staff') return staffRoleName ? `Nhân viên · ${staffRoleName}` : 'Nhân viên'
  if (tenantType === 'agency' || role === 'agency') {
    return 'Chủ Đại lý'
  }
  if (tenantType === 'ctv' || role === 'ctv') {
    return 'Cộng tác viên'
  }
  if (tenantType === 'admin' || tenantType === 'super_admin') {
    switch (role) {
      case 'operations': return 'Admin Vận hành'
      case 'accountant': return 'Admin Kế toán'
      default: return 'Quản trị viên'
    }
  }
  return 'Chủ shop'
}

export interface AccountLevelInfo {
  levelTag: string
  levelName: string
  roleTitle: string
  badgeClass: string
  dotClass: string
}

/**
 * Lấy thông tin vai trò mô hình của tài khoản trong hệ thống ShopTrack:
 * - Super Admin: Quản trị tối cao
 * - Admin: Quản trị viên (Vận hành / Kế toán)
 * - Đại lý: Đại lý phân phối (Agency Hub)
 * - CTV: Cộng tác viên (CTV Portal)
 * - Shop: Điểm bán lẻ (Store & POS)
 */
export function getAccountLevelInfo(tenantType?: string | null, role?: string | null): AccountLevelInfo {
  if (tenantType === 'super_admin') {
    return {
      levelTag: 'Super Admin',
      levelName: 'Quản trị tối cao SaaS',
      roleTitle: 'Super Administrator',
      badgeClass: 'bg-danger/10 text-danger border-danger/30',
      dotClass: 'bg-danger',
    }
  }

  if (tenantType === 'admin') {
    const adminRoleTitle = role === 'operations' ? 'Admin Vận hành (Duyệt CRUD)' : role === 'accountant' ? 'Admin Kế toán' : 'Quản trị viên hệ thống'
    return {
      levelTag: 'Admin',
      levelName: 'Quản trị viên hệ thống',
      roleTitle: adminRoleTitle,
      badgeClass: 'bg-danger/10 text-danger border-danger/30',
      dotClass: 'bg-danger',
    }
  }

  if (tenantType === 'agency' || role === 'agency') {
    return {
      levelTag: 'Đại lý',
      levelName: 'Đại lý phân phối',
      roleTitle: 'Chủ Đại lý phân phối',
      badgeClass: 'tone-orange bg-toneBg text-toneFg border-toneFg/25',
      dotClass: 'tone-orange bg-toneFg',
    }
  }

  if (tenantType === 'ctv' || role === 'ctv') {
    return {
      levelTag: 'CTV',
      levelName: 'Cộng tác viên (CTV)',
      roleTitle: 'Cộng tác viên Affiliate',
      badgeClass: 'tone-violet bg-toneBg text-toneFg border-toneFg/25',
      dotClass: 'tone-violet bg-toneFg',
    }
  }

  // Shop / Điểm bán lẻ
  return {
    levelTag: 'Shop',
    levelName: 'Shop / Điểm bán lẻ',
    roleTitle: 'Chủ shop',
    badgeClass: 'tone-green bg-toneBg text-toneFg border-toneFg/25',
    dotClass: 'tone-green bg-toneFg',
  }
}

export type RoleTheme = 'agency' | 'ctv' | 'shop' | 'npp'

/**
 * Xác định mã giao diện màu sắc tương ứng với từng vai trò:
 * - 'agency': Quản lý / Đại lý -> Màu vàng kim (Royal Gold / Warm Amber)
 * - 'ctv': Cộng tác viên -> Màu tím (Modern Purple / Violet)
 * - 'shop': Shop bán lẻ -> Màu như hiện tại (E-commerce Blue)
 */
export function getRoleTheme(tenantType?: string | null, role?: string | null): RoleTheme {
  if (tenantType === 'agency' || tenantType === 'admin' || tenantType === 'super_admin' || role === 'agency') {
    return 'agency'
  }
  if (tenantType === 'ctv' || role === 'ctv') {
    return 'ctv'
  }
  return 'shop'
}


